import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Avatar,
    Chip,
    Fade,
    Zoom,
    useTheme
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    Hotel,
    Nature,
    Restaurant,
    SportsSoccer,
    FitnessCenter,
    ContentCut,
    Star,
    Schedule,
    MonetizationOn,
    People,
    ArrowForward,
    Business,
    Phone,
    Email,
    LocationOn
} from '@mui/icons-material';
import '../cat-theme.css';
import CatLoadingSpinner from './CatLoadingSpinner';

interface Business {
    id: string;
    name: string;
    description: string;
    slug: string;
    contactInfo?: {
        phone?: string;
        email?: string;
        address?: string;
    };
}

interface Board {
    id: string;
    name: string;
    description: string;
    slug: string;
    type: string;
    capacity: number;
    pricing?: {
        basePrice?: number;
        perHour?: number;
        perDay?: number;
    };
    images?: string[];
}

interface PublicBusinessLandingProps {
    businessSlug?: string;
}

const PublicBusinessLanding: React.FC<PublicBusinessLandingProps> = ({ businessSlug: propBusinessSlug }) => {
    const { businessSlug: paramBusinessSlug } = useParams<{ businessSlug: string }>();
    const businessSlug = propBusinessSlug || paramBusinessSlug;
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [business, setBusiness] = useState<Business | null>(null);
    const [boards, setBoards] = useState<Board[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (businessSlug) {
            fetchBusinessData();
        }
    }, [businessSlug]);

    const fetchBusinessData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`/api/public/business/${businessSlug}`);
            setBusiness(response.data.business);
            setBoards(response.data.boards);
        } catch (err) {
            console.error('Error fetching business data:', err);
            setError('Error al cargar la información del negocio');
        } finally {
            setLoading(false);
        }
    };

    const getBoardIcon = (type: string) => {
        switch (type) {
            case 'rooms': return <Hotel sx={{ fontSize: 40, color: 'white' }} />;
            case 'cabins': return <Nature sx={{ fontSize: 40, color: 'white' }} />;
            case 'camping': return <Nature sx={{ fontSize: 40, color: 'white' }} />;
            case 'salons': return <Restaurant sx={{ fontSize: 40, color: 'white' }} />;
            case 'sports_fields': return <SportsSoccer sx={{ fontSize: 40, color: 'white' }} />;
            case 'appointments': return <Schedule sx={{ fontSize: 40, color: 'white' }} />;
            case 'equipment': return <FitnessCenter sx={{ fontSize: 40, color: 'white' }} />;
            default: return <ContentCut sx={{ fontSize: 40, color: 'white' }} />;
        }
    };

    const getBoardTypeLabel = (type: string) => {
        switch (type) {
            case 'rooms': return 'Habitaciones';
            case 'cabins': return 'Cabañas';
            case 'camping': return 'Camping';
            case 'salons': return 'Salones';
            case 'sports_fields': return 'Canchas';
            case 'appointments': return 'Citas';
            case 'equipment': return 'Equipos';
            default: return 'Servicio';
        }
    };

    const getPricingDisplay = (pricing?: Board['pricing']) => {
        if (!pricing) return 'Consultar precio';

        if (pricing.perDay) return `Desde $${pricing.perDay}/día`;
        if (pricing.perHour) return `Desde $${pricing.perHour}/hora`;
        if (pricing.basePrice) return `$${pricing.basePrice}`;

        return 'Consultar precio';
    };

    const handleBoardClick = (board: Board) => {
        navigate(`/public/board/${board.id}`);
    };

    if (!businessSlug) {
        return (
            <Box p={2}>
                <Alert severity="error">No se encontró el slug del negocio</Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" className="cat-paw-background">
                <CatLoadingSpinner message="Cargando servicios..." />
            </Box>
        );
    }

    return (
        <Box
            className="cat-paw-background"
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #FFF5F8 0%, #E8D5F2 100%)',
                position: 'relative',
                overflow: 'hidden',
                pb: 4
            }}
        >
            <Box sx={{ position: 'relative', zIndex: 2, p: 3 }}>
                <Container maxWidth="lg">
                    {error && (
                        <Zoom in>
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                                {error}
                            </Alert>
                        </Zoom>
                    )}

                    {business && (
                        <Fade in timeout={500}>
                            <Box>
                                {/* Business Header */}
                                <Paper
                                    className="cat-card"
                                    elevation={8}
                                    sx={{
                                        p: 4,
                                        mb: 4,
                                        background: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)',
                                        backdropFilter: 'blur(20px)',
                                        borderRadius: 4,
                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: '#FF6B9D',
                                            width: 80,
                                            height: 80,
                                            mx: 'auto',
                                            mb: 2,
                                            boxShadow: '0 4px 12px rgba(255, 107, 157, 0.3)',
                                            animation: 'cat-bounce 2s infinite'
                                        }}
                                    >
                                        <Business sx={{ fontSize: 40, color: 'white' }} />
                                    </Avatar>

                                    <Typography variant="h3" fontWeight="bold" className="cat-gradient-text" gutterBottom>
                                        {business.name}
                                    </Typography>

                                    {business.description && (
                                        <Typography variant="h6" sx={{ mb: 3, color: isDark ? '#E0E0E0' : '#5D4037', maxWidth: 600, mx: 'auto' }}>
                                            {business.description}
                                        </Typography>
                                    )}

                                    {/* Contact Info */}
                                    {business.contactInfo && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', mt: 2 }}>
                                            {business.contactInfo.phone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Phone sx={{ color: '#FF6B9D' }} />
                                                    <Typography variant="body1">{business.contactInfo.phone}</Typography>
                                                </Box>
                                            )}
                                            {business.contactInfo.email && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Email sx={{ color: '#FF6B9D' }} />
                                                    <Typography variant="body1">{business.contactInfo.email}</Typography>
                                                </Box>
                                            )}
                                            {business.contactInfo.address && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LocationOn sx={{ color: '#FF6B9D' }} />
                                                    <Typography variant="body1">{business.contactInfo.address}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Paper>

                                {/* Boards Section */}
                                {boards.length > 0 ? (
                                    <Box>
                                        <Typography variant="h4" textAlign="center" className="cat-gradient-text" fontWeight="bold" gutterBottom>
                                            Nuestras Agendas
                                        </Typography>
                                        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
                                            Selecciona la agenda que deseas reservar
                                        </Typography>

                                        <Grid container spacing={3}>
                                            {boards.map((board, index) => (
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={board.id}>
                                                    <Fade in timeout={500 + index * 100}>
                                                        <Card
                                                            className="cat-card"
                                                            elevation={6}
                                                            sx={{
                                                                height: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                background: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)',
                                                                backdropFilter: 'blur(20px)',
                                                                borderRadius: 3,
                                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
                                                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                                '&:hover': {
                                                                    transform: 'translateY(-4px)',
                                                                    boxShadow: '0 8px 25px rgba(155, 89, 182, 0.3)'
                                                                }
                                                            }}
                                                        >
                                                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                                <Box display="flex" alignItems="center" mb={2}>
                                                                    <Avatar
                                                                        sx={{
                                                                            bgcolor: '#FF6B9D',
                                                                            mr: 2,
                                                                            width: 50,
                                                                            height: 50
                                                                        }}
                                                                    >
                                                                        {getBoardIcon(board.type)}
                                                                    </Avatar>
                                                                    <Box>
                                                                        <Typography variant="h6" fontWeight="bold" className="cat-gradient-text">
                                                                            {board.name}
                                                                        </Typography>
                                                                        <Chip
                                                                            label={getBoardTypeLabel(board.type)}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: '#E8D5F2',
                                                                                color: '#9B59B6',
                                                                                fontSize: '0.7rem'
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Box>

                                                                {board.description && (
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.4 }}>
                                                                        {board.description}
                                                                    </Typography>
                                                                )}

                                                                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <People sx={{ fontSize: 16, color: '#2E7D32' }} />
                                                                        <Typography variant="body2" color="#2E7D32">
                                                                            {board.capacity} personas
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <MonetizationOn sx={{ fontSize: 16, color: '#EF6C00' }} />
                                                                        <Typography variant="body2" color="#EF6C00">
                                                                            {getPricingDisplay(board.pricing)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </CardContent>

                                                            <CardActions sx={{ p: 3, pt: 0 }}>
                                                                <Button
                                                                    fullWidth
                                                                    variant="contained"
                                                                    endIcon={<ArrowForward />}
                                                                    onClick={() => handleBoardClick(board)}
                                                                    className="cat-button"
                                                                    sx={{
                                                                        background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
                                                                        boxShadow: '0 4px 15px rgba(155, 89, 182, 0.4)',
                                                                        borderRadius: 3,
                                                                        '&:hover': {
                                                                            background: 'linear-gradient(135deg, #9B59B6 0%, #FF6B9D 100%)',
                                                                            transform: 'translateY(-2px)'
                                                                        }
                                                                    }}
                                                                >
                                                                    Reservar Ahora
                                                                </Button>
                                                            </CardActions>
                                                        </Card>
                                                    </Fade>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Paper
                                        elevation={4}
                                        sx={{
                                            p: 4,
                                            textAlign: 'center',
                                            background: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)',
                                            borderRadius: 3
                                        }}
                                    >
                                        <Typography variant="h6" color="text.secondary">
                                            No hay servicios disponibles en este momento.
                                        </Typography>
                                    </Paper>
                                )}
                            </Box>
                        </Fade>
                    )}
                </Container>
            </Box>
        </Box>
    );
};

export default PublicBusinessLanding;